import { useEffect, useState } from "react";
import { Loader2, Lock, TriangleAlert, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { useAuth } from "../auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const ENTITIES = ["persona_fisica", "srl", "ditta_individuale"];
const BUSINESS = ["srl", "ditta_individuale"];

export default function KycModal({ open, onOpenChange, onDone, context = "purchase" }) {
  const { t } = useLanguage();
  const { user, checkAuth } = useAuth();
  const docRequested = !!user?.kyc_document_requested;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [country, setCountry] = useState("");
  const [entity, setEntity] = useState("persona_fisica");
  const [companyName, setCompanyName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [sdiCode, setSdiCode] = useState("");
  const [pec, setPec] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthCity, setBirthCity] = useState("");
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prevKyc, setPrevKyc] = useState(null);
  const [confirmChange, setConfirmChange] = useState(false);
  const [ordersCount, setOrdersCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    checkAuth();
    // Precompila con i dati fiscali già salvati; se mancano, nome e cognome
    // arrivano dalla registrazione (user.name)
    api.get("/kyc").then(({ data }) => {
      const k = data.kyc;
      const nm = (user?.name || "").trim().split(/\s+/).filter(Boolean);
      const regFirst = nm[0] || "";
      const regLast = nm.slice(1).join(" ") || "";
      if (!k) {
        setFirstName(regFirst);
        setLastName(regLast);
        return;
      }
      setPrevKyc(k);
      setFirstName(k.first_name || regFirst);
      setLastName(k.last_name || regLast);
      setTaxId(k.tax_id || "");
      setCountry(k.country || "");
      setEntity(k.tax_entity_type || (k.account_type === "business" ? "srl" : "persona_fisica"));
      setCompanyName(k.company_name || "");
      setVatNumber(k.vat_number || "");
      setSdiCode(k.sdi_code || "");
      setPec(k.pec || "");
      setBirthDate(k.birth_date || "");
      setBirthCity(k.birth_city || "");
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Tipo societario DEFINITIVO: chi è già azienda (srl/ditta) non può più cambiarlo
  const lockedEntity = prevKyc && BUSINESS.includes(prevKyc.tax_entity_type) ? prevKyc.tax_entity_type : null;

  const isBusiness = entity !== "persona_fisica";
  const needsTaxId = entity !== "srl";
  // Sta passando ad azienda (non lo era già): avviso di irreversibilità
  const switchingToBusiness = isBusiness && entity !== lockedEntity;
  // Tono aziendale quando l'account è business/dealer OPPURE si sta scegliendo una forma societaria
  const businessTone = isBusiness || !!user?.is_dealer || user?.kyc_account_type === "business";
  // Contesto: "topup" (ricarica plafond) ha testi dedicati, diversi dall'attivazione eSIM
  const titleKey = businessTone ? "kyc.titleBiz" : context === "topup" ? "kyc.titleTopup" : "kyc.title";
  const subKey = businessTone ? "kyc.subBiz" : context === "topup" ? "kyc.subTopup" : "kyc.sub";

  const payload = () => ({
    first_name: firstName.trim(),
    last_name: lastName.trim(),
    tax_id: taxId.trim(),
    country: country.trim().toUpperCase(),
    account_type: isBusiness ? "business" : "private",
    tax_entity_type: entity,
    company_name: companyName.trim(),
    vat_number: vatNumber.trim(),
    sdi_code: sdiCode.trim(),
    pec: pec.trim(),
    birth_date: isBusiness ? "" : birthDate,
    birth_city: isBusiness ? "" : birthCity.trim(),
  });

  const fiscalChanged = () => {
    if (!prevKyc) return false;
    const p = payload();
    return ["first_name", "last_name", "tax_id", "country", "account_type", "tax_entity_type", "company_name", "vat_number", "sdi_code", "pec", "birth_date", "birth_city"]
      .some((f) => String(prevKyc[f] || "") !== String(p[f] || ""));
  };

  const doSave = async () => {
    setBusy(true);
    try {
      await api.post("/kyc", payload());
      if (doc) {
        const fd = new FormData();
        fd.append("file", doc);
        await api.post("/kyc/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setConfirmChange(false);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      const known = {
        tax_id_birth_mismatch: t("kyc.errBirthMismatch"),
        invalid_birth_date: t("kyc.errBirthDate"),
        business_irreversible: t("kyc.errIrreversible"),
        sdi_or_pec_required: t("kyc.errSdiPec"),
      };
      toast.error(known[d] || d || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Aziende: almeno uno tra codice SDI e PEC (consegna fattura elettronica)
    if (isBusiness && !sdiCode.trim() && !pec.trim()) {
      toast.error(t("kyc.errSdiPec"));
      return;
    }
    if (docRequested && !doc) {
      toast.error(t("kyc.docMissing"));
      return;
    }
    // Doppia conferma: dati fiscali cambiati e ci sono già ordini emessi
    if (fiscalChanged()) {
      try {
        const { data } = await api.get("/orders");
        const n = (data.data || []).length;
        if (n > 0) {
          setOrdersCount(n);
          setConfirmChange(true);
          return;
        }
      } catch { /* se non riesco a contare, procedo */ }
    }
    await doSave();
  };

  const inputCls = "w-full rounded-2xl border border-slate-200 bg-white px-5 py-3 text-base sm:text-sm outline-none focus:border-[#FF6B35] transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="kyc-dialog" aria-describedby={undefined} className="max-w-[calc(100vw-2rem)] sm:max-w-md p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display" data-testid="kyc-title">{t(titleKey)}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600" data-testid="kyc-sub">{t(subKey)}</p>

        {confirmChange ? (
          <div data-testid="kyc-confirm-change" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-5">
            <div className="flex items-start gap-3">
              <TriangleAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display font-bold text-slate-900">{t("kyc.confirmTitle")}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                  {t("kyc.confirmBody").replace("{n}", ordersCount)}
                </p>
                <div className="mt-4 flex gap-2">
                  <button data-testid="kyc-confirm-yes" onClick={doSave} disabled={busy}
                    className="rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center gap-2">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t("kyc.confirmYes")}
                  </button>
                  <button data-testid="kyc-confirm-no" onClick={() => setConfirmChange(false)} disabled={busy}
                    className="rounded-full border border-slate-300 px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                    {t("kyc.confirmNo")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <div className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">{t("kyc.entityType")}</div>
            <div className="flex gap-2">
              {ENTITIES.map((k) => {
                const locked = !!lockedEntity && k !== lockedEntity;
                return (
                  <button type="button" key={k} data-testid={`kyc-entity-${k}`} onClick={() => !locked && setEntity(k)} disabled={locked}
                    className={`flex-1 rounded-full px-2 py-2.5 font-mono-t text-[9px] font-bold uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${entity === k ? "bg-[#FF6B35] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"}`}>
                    {t(`kyc.entity.${k}`)}
                  </button>
                );
              })}
            </div>
            {lockedEntity && (
              <p data-testid="kyc-entity-locked" className="mt-2 flex items-center gap-1.5 font-mono-t text-[10px] uppercase tracking-widest text-slate-400">
                <Lock className="w-3 h-3" /> {t("kyc.entityLocked")}
              </p>
            )}
          </div>
          {switchingToBusiness && (
            <div data-testid="kyc-irreversible-warning" className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-2.5">
              <TriangleAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold text-sm text-slate-900">{t("kyc.irrevTitle")}</div>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">{t("kyc.irrevBody")}</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="kyc-first-name" required value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={t("kyc.firstName")} className={inputCls} />
            <input data-testid="kyc-last-name" required value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={t("kyc.lastName")} className={inputCls} />
          </div>
          {isBusiness && (
            <input data-testid="kyc-company-name" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={t("kyc.companyName")} className={inputCls} />
          )}
          {needsTaxId && (
            <input data-testid="kyc-tax-id" required minLength={16} maxLength={16} value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder={t("kyc.taxId")} className={`${inputCls} font-mono-t uppercase`} />
          )}
          {isBusiness && (
            <input data-testid="kyc-vat-number" required minLength={11} maxLength={11} value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} placeholder={t("kyc.vatNumber")} className={`${inputCls} font-mono-t uppercase`} />
          )}
          {!isBusiness && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">{t("kyc.birthDate")}</div>
                <input data-testid="kyc-birth-date" required type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} className={inputCls} />
              </div>
              <div>
                <div className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-transparent mb-1.5 select-none hidden sm:block">.</div>
                <input data-testid="kyc-birth-city" required value={birthCity} onChange={(e) => setBirthCity(e.target.value)} placeholder={t("kyc.birthCity")} className={inputCls} />
              </div>
            </div>
          )}
          <input data-testid="kyc-country" required minLength={2} maxLength={2} value={country} onChange={(e) => setCountry(e.target.value)} placeholder={t("kyc.country")} className={`${inputCls} uppercase`} />
          {isBusiness && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="kyc-sdi-code" value={sdiCode} minLength={7} maxLength={7} onChange={(e) => setSdiCode(e.target.value)} placeholder={t("kyc.sdiCode")} className={`${inputCls} font-mono-t uppercase`} />
              <input data-testid="kyc-pec" type="email" value={pec} onChange={(e) => setPec(e.target.value)} placeholder={t("kyc.pec")} className={inputCls} />
            </div>
          )}
          {isBusiness && <p className="text-xs text-slate-400">{t("kyc.sdiHint")}</p>}
          {docRequested && (
            <label data-testid="kyc-doc-field" className="flex items-center gap-3 rounded-2xl border border-dashed border-[#FF6B35]/60 bg-orange-50/50 px-5 py-3.5 text-sm text-slate-600 cursor-pointer hover:border-[#FF6B35] transition-colors">
              <Upload className="w-4 h-4 shrink-0" />
              <span className="truncate">{doc ? doc.name : t("kyc.docReq")}</span>
              <input data-testid="kyc-document-input" type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={(e) => setDoc(e.target.files?.[0] || null)} />
            </label>
          )}
          <button data-testid="kyc-submit" type="submit" disabled={busy}
            className="w-full rounded-full bg-[#FF6B35] text-white px-6 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("kyc.submit")}
          </button>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
